import * as React from "react";
import { useState } from "react";
import { Box, Stack } from "@mui/material";
import NavBar from "../../../NavBar";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./styles.css";
import {
  editVehicleDetails,
  setSelectedVehicleDetails,
} from "../../../redux/vehicle/action";
import { retreiveURL } from "../../../utils";
import { enqueueSnackbar } from "notistack";
import UpdateTruckForm from "../../../components/forms/UpdateTruckFrom";
import UpdateTrailerForm from "../../../components/forms/UpdateTrailerForm";
import UpdatePersonalVehicleForm from "../../../components/forms/UpdatePersonalVehicleForm";
import vehiclePlaceHolderImage from "../../../assets/images/vehicle_placeholder.jpeg";
import { useResponsive } from "../../../hooks/useResponsive";
import { useRef } from "react";
import MenuBar from "../../menuBar";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import storePersist, { localConstant } from "../../../services/localStorage";
import SnackbarConfig from "../../../utils/snackbarConfig";
import { useTranslation } from "react-i18next";

const EditVehicle = ({ enableNav = true, isDialog = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { current } = useSelector((state) => state.loginReducer);
  const { selectedVehicle } = useSelector(
    (state) => state.setSelectedVehicleReducer,
  );

  const smDown = useResponsive("down", "sm");
  const { t } = useTranslation();

  const [dialogProps, setDialogProps] = useState({
    isOpen: false,
    title: `${t("pages.profile.key07")} ?`,
    content: `${t("pages.profile.key13")}`,
    reset: () => setDialogProps((prev) => ({ ...prev, isOpen: false })),
  });

  const [uploadFile] = useState({
    type: selectedVehicle?.mediaTypeCode ? selectedVehicle?.mediaTypeCode : "",
  });

  const [uploadedImage] = useState(
    selectedVehicle.bulkTempUrl
      ? retreiveURL(selectedVehicle.bulkTempUrl)
      : vehiclePlaceHolderImage,
  );

  const urlToObject = async (imageUrl) => {
    const response = await fetch(imageUrl);
    // here image is url/location of image
    const blob = await response.blob();
    const file = new File([blob], "image.jpg", { type: blob.type });
    return file;
  };

  const getVehicleType = (key) => {
    switch (key) {
      case "TRAILER":
        return "TRALR";

      case "PERSONALV":
        return "PERVEH";

      default:
        return key;
    }
  };

  const submitForm = async (
    values,
    isTruckFileRemoved,
    isLicensePlateRemoved,
    isInsuranceRemoved,
    uploadTruckImage,
    uploadLicensePlate,
    uploadInsuranceFile,
  ) => {
    values.customerVehId = selectedVehicle?.customerVehicleId;
    values.veh_type_cd = getVehicleType(
      selectedVehicle?.veh_type_cd?.toString()?.toUpperCase(),
    );
    values.createBy = current.entity_first_name;
    values.cust_id = current?.entity_customer_id
      ? current?.entity_customer_id
      : storePersist.get(localConstant.ENTITY_CUSTOMER_ID);
    values.lic_plate_num = selectedVehicle?.lic_plate_num;
    values.crt_user = current.entity_first_name;

    let imageObject;
    if (!uploadFile) imageObject = await urlToObject(uploadedImage);
    else imageObject = uploadFile;

    let fileInfoArray = [];
    let deleteOldMedia = [];

    if (uploadTruckImage || isTruckFileRemoved) {
      fileInfoArray.push({
        name: "file0",
        mediaType: uploadTruckImage
          ? uploadTruckImage?.type === "application/pdf"
            ? "PDF"
            : uploadTruckImage?.type
          : "",
        mediaCode:
          String(selectedVehicle?.veh_type_cd).toUpperCase() === "TRUCK"
            ? "VEHCLPIC"
            : "LICPL",
        mediaObj: uploadTruckImage,
      });

      const fileMedialObj = selectedVehicle?.fileUploadResponse?.filter((fm) =>
        String(selectedVehicle?.veh_type_cd).toUpperCase() === "TRUCK"
          ? fm.mediaCode === "VEHCLPIC"
          : fm.mediaCode === "LICPL",
      );

      if (fileMedialObj && fileMedialObj?.[0]?.mediaId)
        deleteOldMedia.push(fileMedialObj[0].mediaId);
    }

    if (uploadLicensePlate || isLicensePlateRemoved) {
      fileInfoArray.push({
        name: "file1",
        mediaType: uploadLicensePlate
          ? uploadLicensePlate?.type === "application/pdf"
            ? "PDF"
            : uploadLicensePlate?.type
          : "",
        mediaCode: "LICPL",
        mediaObj: uploadLicensePlate,
      });
      const fileMedialObj = selectedVehicle?.fileUploadResponse?.filter(
        (fm) => fm.mediaCode === "LICPL",
      );
      if (fileMedialObj && fileMedialObj?.[0]?.mediaId)
        deleteOldMedia.push(fileMedialObj[0].mediaId);
    }

    if (uploadInsuranceFile || isInsuranceRemoved) {
      fileInfoArray.push({
        name: `file${fileInfoArray.length}`,
        mediaType: uploadInsuranceFile
          ? uploadInsuranceFile?.type === "application/pdf"
            ? "PDF"
            : uploadInsuranceFile?.type
          : "",
        mediaCode: "PROOFINS",
        mediaObj: uploadInsuranceFile,
      });

      const insuranceMediaObj = selectedVehicle?.fileUploadResponse?.find(
        (fm) => fm.mediaCode === "PROOFINS",
      );

      if (insuranceMediaObj?.mediaId)
        deleteOldMedia.push(insuranceMediaObj.mediaId);
    }

    values.fileInfosArray = fileInfoArray;
    values.deleteOldMedia = deleteOldMedia;
    setDialogProps((prev) => ({
      ...prev,
      isOpen: true,
      handleContinue: () => {
        setIsLoading(true);
        dispatch(
          editVehicleDetails(values, imageObject, (updateVehicleResponse) => {
            setIsLoading(false);
            if (updateVehicleResponse?.status === 200) {
              dispatch(setSelectedVehicleDetails(updateVehicleResponse.data));
              enqueueSnackbar(t("pages.editVehicle.pageHeading"), {
                variant: "success",
                autoHideDuration: 3000,
                action: SnackbarConfig,
              });
              if (smDown) {
                navigate(-2);
              } else {
                navigate(-1);
              }
            } else {
              if (updateVehicleResponse?.response?.data?.errorMessage) {
                enqueueSnackbar(
                  updateVehicleResponse?.response?.data?.errorMessage,
                  {
                    variant: "error",
                    autoHideDuration: 3000,
                    action: SnackbarConfig,
                  },
                );
              } else {
                enqueueSnackbar(
                  "Invalid request. Unable to proceed your action.",
                  {
                    variant: "error",
                    autoHideDuration: 3000,
                    action: SnackbarConfig,
                  },
                );
              }
            }
          }),
        );
      },
    }));
  };

  const myRef = useRef({});

  return (
    <div
      style={{
        width: "100%",
        marginLeft: 0,
      }}
    >
      {enableNav && (
        <NavBar title={t("pages.editVehicle.pageHeading")} enableMenu={false} />
      )}
      <MenuBar myRef={myRef} />
      <Box
        sx={{
          width: "100%",
          margin: "auto",
        }}
      >
        <Stack spacing={2} sx={{ justifyContent: "space-between", padding: 2 }}>
          {(selectedVehicle?.veh_type_cd === "Truck" ||
            selectedVehicle?.veh_type_cd === "TRUCK") && (
            <UpdateTruckForm
              disableSubmit={isLoading}
              submitForm={(
                data,
                isTruckFileRemoved,
                isLicensePlateRemoved,
                isInsuranceRemoved,
                uploadTruckFile,
                uploadLicensePlate,
                uploadInsuranceFile,
              ) => {
                submitForm(
                  data,
                  isTruckFileRemoved,
                  isLicensePlateRemoved,
                  isInsuranceRemoved,
                  uploadTruckFile,
                  uploadLicensePlate,
                  uploadInsuranceFile,
                );
              }}
              selectedVehicle={selectedVehicle}
            />
          )}
          {(selectedVehicle?.veh_type_cd === "Trailer" ||
            selectedVehicle?.veh_type_cd === "TRAILER" ||
            selectedVehicle?.veh_type_cd === "TRALR") && (
            <UpdateTrailerForm
              disableSubmit={isLoading}
              submitForm={(
                data,
                isTruckFileRemoved,
                isLicensePlateRemoved,
                isInsuranceRemoved,
                uploadTruckImage,
                uploadLicensePlate,
                uploadInsuranceFile,
              ) => {
                submitForm(
                  data,
                  false,
                  isLicensePlateRemoved,
                  isInsuranceRemoved,
                  null,
                  uploadLicensePlate,
                  uploadInsuranceFile,
                );
              }}
              selectedVehicle={selectedVehicle}
            />
          )}
          {(selectedVehicle?.veh_type_cd === "PersonalV" ||
            selectedVehicle?.veh_type_cd === "PERVEH") && (
            <UpdatePersonalVehicleForm
              disableSubmit={isLoading}
              submitForm={(
                data,
                isTruckFileRemoved,
                isLicensePlateRemoved,
                isInsuranceRemoved,
                uploadTruckImage,
                uploadLicensePlate,
                uploadInsuranceFile,
              ) => {
                submitForm(
                  data,
                  isTruckFileRemoved,
                  isLicensePlateRemoved,
                  isInsuranceRemoved,
                  uploadTruckImage,
                  uploadLicensePlate,
                  uploadInsuranceFile,
                );
              }}
              selectedVehicle={selectedVehicle}
            />
          )}
        </Stack>
      </Box>
      {dialogProps.isOpen && <ConfirmationDialog {...dialogProps} />}
    </div>
  );
};

export default EditVehicle;
